import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us | KambiHome - Your Curated AliExpress Portal</title>
        <meta name="description" content="Discover the story behind KambiHome, our mission to simplify AliExpress discovery, and why users trust us. Learn how KambiHome works." />
        <meta name="keywords" content="KambiHome, About KambiHome, AliExpress Nigeria, Curated Shopping, Product Discovery, E-commerce" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://kambihome.com/about" />
        <meta property="og:title" content="About Us | KambiHome" />
        <meta property="og:description" content="Discover the story behind KambiHome and how we're simplifying AliExpress discovery for Nigeria and the diaspora." />
        <meta property="og:url" content="https://kambihome.com/about" />
      </Helmet>

      <Header />

      <main className="mt-16 text-gray-800 scroll-smooth">
        {/* Hero Section */}
        <section className="bg-green-50 py-20 px-4 text-center animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">About KambiHome</h1>
          <p className="text-lg max-w-2xl mx-auto">
            Your trusted gateway to AliExpress product discovery, built for simplicity, speed, and smart shopping.
          </p>
        </section>

        {/* Our Story */}
        <section className="py-16 px-4 bg-white animate-slide-up">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <p className="text-lg leading-relaxed">
              KambiHome was born out of the need to make AliExpress easier to navigate, especially for shoppers in regions like Nigeria.
              With thousands of products and categories, it’s easy to get overwhelmed. We created KambiHome to filter the noise, spotlight top picks,
              and help users discover what they love, faster and smarter.
            </p>
          </div>
        </section>

        {/* What We Offer */}
        <section className="bg-gray-50 py-16 px-4 animate-fade-in">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">What We Offer</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Curated Categories',
                  desc: 'Discover AliExpress products grouped under smart parent–child categories for faster navigation.'
                },
                {
                  title: 'Localized Sync',
                  desc: 'Enjoy product previews optimized for our audiences, with price insights and real-time updates.'
                },
                {
                  title: 'AliExpress Integration',
                  desc: 'Each product links directly to AliExpress — no extra charges or intermediaries.'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition duration-300">
                  <h3 className="font-semibold text-xl mb-2">{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why KambiHome */}
        <section className="py-16 px-4 bg-white animate-slide-up">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 text-center">Why Choose KambiHome?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Speed & Simplicity',
                  desc: 'Faster loading, clean UI, and smart search make finding products effortless.'
                },
                {
                  title: 'Verified Sync',
                  desc: 'Real-time API sync with AliExpress for updated product listings and shipping data.'
                },
                {
                  title: 'SEO-Optimized Listings',
                  desc: 'Every product and category is structured for Google discoverability.'
                }
              ].map((item, index) => (
                <div key={index} className="p-6 rounded-lg bg-green-50 border shadow hover:shadow-md transition duration-300">
                  <h4 className="font-semibold text-lg mb-1">{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-gray-100 py-16 px-4 animate-fade-in">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-6 text-left">
              {['Browse categories or type keywords', 'Preview curated AliExpress results', 'Click to view product details', 'Buy instantly'].map(
                (step, idx) => (
                  <div key={idx}>
                    <span className="text-green-600 font-bold">Step {idx + 1}</span>
                    <p className="font-medium">{step}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        {/* Mission and Vision */}
        <section className="py-16 px-4 bg-white animate-slide-up">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Our Mission & Vision</h2>
            <p className="text-lg leading-relaxed mb-4">
              At KambiHome, our mission is to simplify online shopping for emerging markets. We envision a platform that bridges global
              e-commerce with local relevance, helping millions of users find the best deals without hassle.
            </p>
            <p className="text-lg leading-relaxed">
              We’re not just listing products, we’re building trust in global shopping through smart technology and curated experience.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-green-100 text-black py-16 px-4 text-center animate-fade-in">
          <h2 className="text-3xl font-bold mb-4">Ready to Explore?</h2>
          <p className="mb-6">Dive into thousands of curated AliExpress products tailored for you.</p>
          <a
            href="/products"
            className="inline-block bg-white text-green-600 px-6 py-3 rounded-md font-semibold hover:bg-gray-100 transition shadow"
          >
            Explore Products
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AboutPage;
